import { skipToken } from '@reduxjs/toolkit/query'
import { useCallback, useMemo } from 'react'

import { useGetScopedUidGroupsQuery } from 'api/canonicalPicker'
import type { PickerRowKind, UidGroupRow } from 'api/canonicalPicker.types'
import type { PickerSource } from 'slices/aiConversations/pickerTypes'

import type { UidPaste } from './useUidSearch'

type Args = {
  search: UidPaste | null
  source: PickerSource
  kind: PickerRowKind
  scope: string[] | null
  /** The smart source naming a scoped level's products; null when the level isn't scoped */
  scopeSource: PickerSource | null
  retrySearch: () => void
}

const NO_GROUPS: UidGroupRow[] = []

/**
 * A UID search on a fixed list scoped by level 1: the groups it finds within
 * the scope stay pickable, and those outside it are set apart, so a UID that
 * exists elsewhere in the catalog is never missing nor read as not found.
 */
const useScopedUidSearch = ({
  search,
  source,
  kind,
  scope,
  scopeSource,
  retrySearch,
}: Args) => {
  const isScoped = kind === 'uidGroup' && scopeSource !== null && scope !== null
  const scoped = useGetScopedUidGroupsQuery(
    isScoped && search && scopeSource && scope
      ? { source, scopeSource, scope }
      : skipToken
  )
  const { currentData, isError, refetch } = scoped

  const result = useMemo((): {
    search: UidPaste | null
    outOfScope: UidGroupRow[]
  } => {
    if (!search || !isScoped) return { search, outOfScope: NO_GROUPS }
    if (search.status === 'failed' || isError) {
      return { search: { ...search, status: 'failed' }, outOfScope: NO_GROUPS }
    }
    if (search.status === 'checking' || !currentData) {
      return {
        search: { ...search, status: 'checking' },
        outOfScope: NO_GROUPS,
      }
    }
    const inScope = new Set(currentData.map(group => group.id))
    return {
      search: {
        ...search,
        groups: search.groups.filter(group => inScope.has(group.id)),
      },
      outOfScope: search.groups.filter(group => !inScope.has(group.id)),
    }
  }, [currentData, isError, isScoped, search])

  const retry = useCallback(() => {
    if (isError) refetch()
    if (search?.status === 'failed') retrySearch()
  }, [isError, refetch, retrySearch, search?.status])

  return { ...result, retry }
}

export default useScopedUidSearch
